// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {Component, DestroyRef, EventEmitter, inject, OnInit, Output, ViewEncapsulation} from "@angular/core";
import {AppScope} from "../services/app_scope.service";
import {MatDialog} from "@angular/material/dialog";
import {DateTimePeriodService} from "../services/date-time-period.service";
import {HeaderDataService} from "../services/header_data.service";
import {UploadFormDialogComponent} from "../forms/upload-form/upload-form.component";
import {ApiService} from "../services/api/api.service";
import {PresentationService} from "../services/presentation.service";
import {ThemeService} from "../services/theme.service";
import {forkJoin, switchMap, take} from "rxjs";
import {NotificationService} from "../services/notification.service";
import {COMMON_FORM_IMPORTS, COMMON_MAT_MENU_IMPORTS} from "../shared/import-groups/Common";
import {DateTimePeriodComponent} from "../date-time/date-time-period.component";
import {CommonModule} from "@angular/common";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatIcon} from "@angular/material/icon";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DialogLoaderService} from "../services/dialog-loader.service";

export {MenuItem, IMenu, Menu} from './menu-item.model';

@Component({
    selector: 'header-menu',
    templateUrl: 'header.component.html',
    styleUrls: ['header.component.scss'],
    imports: [
        CommonModule,
        DateTimePeriodComponent,
        COMMON_FORM_IMPORTS,
        COMMON_MAT_MENU_IMPORTS,
        MatSlideToggle,
        MatIcon
    ],
    encapsulation: ViewEncapsulation.None
})

export class HeaderComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogLoader = inject(DialogLoaderService);
    restricted_access: any;
    user: any;
    launch_admin_gui: any;
    session: any;
    @Output()
    finishedLoading: EventEmitter<boolean> = new EventEmitter();

    constructor(public headerData: HeaderDataService,
                public dialog: MatDialog,
                public appScope: AppScope,
                public dateTimePeriodService: DateTimePeriodService,
                public api: ApiService,
                private presentationService: PresentationService,
                private themeService: ThemeService,
                private notification: NotificationService) {
    }

    ngOnInit(): void {
        forkJoin([
            this.dateTimePeriodService.dtpInitialised$.pipe(take(1)),
            this.appScope.currentUserValue.pipe(take(1))
        ]).pipe(
            switchMap(() => {
                this.finishedLoading.emit(true);
                return this.themeService.rightLogoUrl$;
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: logoUrl => this.resolveRightLogo(logoUrl),
            error: err => {
                console.error('WIRE initialisation failed', err);
                this.notification.openError('Failed to load. Please reload the page to retry.');
            }
        });
    }

    private resolveRightLogo(logoUrl: string): void {
        this.headerData.isImage(logoUrl).then((exists) => {
            if (exists) {
                this.headerData.right_logo = logoUrl;
            } else {
                this.headerData.isImage('/admin/myfiles/download/right-logo.png').then((test) => {
                    this.headerData.right_logo = test
                        ? '/admin/myfiles/download/right-logo.png'
                        : '/static/images/right-logo1.png';
                });
            }
        });
    }

    pageCommentButtonClicked() {
        this.headerData.pageCommentButtonClicked.next(!(this.headerData.showing_comments && this.headerData.page_selected));
    }

    printToPdf(): void {
        this.headerData.printToPdfClicked.next();
    }

    uploadData(): void {
        const dialogRef = this.dialog.open(UploadFormDialogComponent, {panelClass: 'upload-form-dialog'});
    }

    async openEditForm() {
        const {EditHistoryComponent} = await this.dialogLoader.load('edit-history', () => import("../forms/edit-history.component"));
        const dialogRef = this.dialog.open(EditHistoryComponent, {panelClass: 'edit-history-form-dialog'});
    }

    togglePresent() {
        this.presentationService.togglePresent();
    }
}
